import "./testimonials.css";

import React from "react";

import { TESTIMONIALS } from "../../data/content";
import PageMeta from "../general/PageMeta";

const SWIPE_DISTANCE = 50;

//Auto-advance timing: a few seconds, plus reading time for the longer testimonials
const MIN_SLIDE_MS = 6000;
const MAX_SLIDE_MS = 16000;
const MS_PER_WORD = 60;

const slideDuration = (testimonial) => {
  const words = testimonial.text.join(" ").split(/\s+/).length;
  return Math.min(MAX_SLIDE_MS, Math.max(MIN_SLIDE_MS, 4000 + words * MS_PER_WORD));
};

const Arrow = ({ direction }) => (
  <svg
    viewBox={"0 0 24 48"}
    aria-hidden={"true"}
  >
    <path d={direction === "left" ? "M20 2L4 24l16 22" : "M4 2l16 22L4 46"} />
  </svg>
);

//Slideshow of client testimonials. It moves on by itself (pausing while the mouse or keyboard focus is on it,
//or when the pause button is used); arrows, dots, keyboard arrows and swiping also change the slide
const Testimonials = () => {
  const [index, setIndex] = React.useState(0);
  const [isPaused, setIsPaused] = React.useState(false); //Pause button
  const [isHeld, setIsHeld] = React.useState(false); //Mouse over / focus inside
  const touchStartX = React.useRef(null);
  const count = TESTIMONIALS.length;

  //Re-runs on every slide change, so using the arrows / dots also restarts the timer
  React.useEffect(() => {
    if (isPaused || isHeld) {
      return undefined;
    }
    const timeout = window.setTimeout(() => setIndex((current) => (current + 1) % count), slideDuration(TESTIMONIALS[index]));
    return () => window.clearTimeout(timeout);
  }, [index, isPaused, isHeld]);

  const onBlur = (e) => {
    //Only resume once focus has left the slideshow altogether
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsHeld(false);
    }
  };

  const go = (step) => setIndex((current) => (current + step + count) % count);

  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft") {
      go(-1);
    }
    else if (e.key === "ArrowRight") {
      go(1);
    }
  };

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) {
      return;
    }
    const distance = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(distance) > SWIPE_DISTANCE) {
      go(distance < 0 ? 1 : -1);
    }
    touchStartX.current = null;
  };

  const testimonial = TESTIMONIALS[index];

  return (
    <section
      className={"testimonials-page"}
      aria-roledescription={"carousel"}
      aria-label={"Client testimonials"}
      onKeyDown={onKeyDown}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onMouseEnter={() => setIsHeld(true)}
      onMouseLeave={() => setIsHeld(false)}
      onFocus={() => setIsHeld(true)}
      onBlur={onBlur}
    >
      <PageMeta
        title={"Client Testimonials"}
        description={"What our clients say about about U Financial Advisors, Discovery Financial Advisors in George."}
      />
      <div
        className={"testimonials-bg"}
        style={{ backgroundImage: "url(/images/pages/testimonials.webp)" }}
        aria-hidden={"true"}
      />
      <h1 className={"visually-hidden"}>Client Testimonials</h1>

      <div className={"testimonials-inner container"}>
        <button
          type={"button"}
          className={"testimonials-arrow is-left"}
          onClick={() => go(-1)}
          aria-label={"Previous testimonial"}
        >
          <Arrow direction={"left"} />
        </button>

        <figure
          key={index}
          className={"testimonial-card"}
          aria-roledescription={"slide"}
          aria-label={`${index + 1} of ${count}`}
          aria-live={isPaused || isHeld ? "polite" : "off"} //Don't announce slides that change by themselves
        >
          <blockquote className={"testimonial-text"}>
            {testimonial.text.map((paragraph) => (
              <p key={paragraph.slice(0, 20)}>{paragraph}</p>
            ))}
          </blockquote>
          <figcaption className={"testimonial-name"}>{testimonial.name}</figcaption>
        </figure>

        <button
          type={"button"}
          className={"testimonials-arrow is-right"}
          onClick={() => go(1)}
          aria-label={"Next testimonial"}
        >
          <Arrow direction={"right"} />
        </button>
      </div>

      <div className={"testimonials-dots"}>
        {TESTIMONIALS.map((t, i) => (
          <button
            key={t.name}
            type={"button"}
            className={`testimonials-dot ${i === index ? "is-active" : ""}`}
            onClick={() => setIndex(i)}
            aria-label={`Show testimonial ${i + 1} (${t.name})`}
            aria-current={i === index ? "true" : undefined}
          />
        ))}
        <button
          type={"button"}
          className={"testimonials-pause"}
          onClick={() => setIsPaused(!isPaused)}
          aria-label={isPaused ? "Play slideshow" : "Pause slideshow"}
        >
          <svg
            viewBox={"0 0 24 24"}
            aria-hidden={"true"}
          >
            <path d={isPaused ? "M8 5v14l11-7z" : "M7 5h4v14H7zM13 5h4v14h-4z"} />
          </svg>
        </button>
      </div>
    </section>
  );
};

export default Testimonials;
